import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatApiError, DRC_CENTER, DRC_MAP_ZOOM, CITY_MAP_ZOOM, formatCDF } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import MapView from "../../components/MapView";
import AiInsightCard from "../../components/AiInsightCard";
import { Button, Badge, Input } from "../../components/ui-kit";
import { ArrowLeft, CalendarBlank, MapPin, NavigationArrow, PlusCircle, X, Ticket, Warning, UserFocus, Motorcycle, Car, Van, Star, Clock, UsersThree, ShieldCheck } from "@phosphor-icons/react";
import AddressSearch from "../../components/AddressSearch";
import { reverseGeocode } from "../../lib/geo";
import { getPreciseLocation } from "../../lib/preciseLocation";

const STEPS = { PICKUP: "pickup", DEST: "dest", REVIEW: "review" };
const RIDE_OPTIONS = [
  "Trajet silencieux",
  "Aide avec bagages",
  "Climatisation",
  "Chauffeur bien note",
  "Recu automatique",
  "Priorite securite",
];

const VEHICLE_META = {
  moto: { icon: Motorcycle, label: "Moto", tone: "text-warning", note: "Le plus rapide en circulation dense" },
  economy: { icon: Car, label: "Economy", tone: "text-primary", note: "Voiture standard, prix malin" },
  comfort: { icon: Car, label: "Comfort", tone: "text-secondary", note: "Plus de confort et d'espace" },
  premium: { icon: Star, label: "Premium", tone: "text-primary", note: "Chauffeur haut de gamme" },
  xl: { icon: Van, label: "NEXORA-XL", tone: "text-warning", note: "Famille, groupe ou bagages" },
};

const DRIVER_COMPATIBILITY = {
  moto: ["moto"],
  economy: ["economy"],
  comfort: ["economy", "comfort"],
  premium: ["economy", "comfort", "premium"],
  xl: ["economy", "comfort", "xl"],
};

export default function RideBooking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [vehicleCats, setVehicleCats] = useState([]);
  const [step, setStep] = useState(STEPS.PICKUP);
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [vehicleType, setVehicleType] = useState("moto");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [allEstimates, setAllEstimates] = useState({});
  const [creating, setCreating] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [nearbyDrivers, setNearbyDrivers] = useState([]);
  const [locating, setLocating] = useState(false);
  const [autoLocationTried, setAutoLocationTried] = useState(false);
  const [scheduleMode, setScheduleMode] = useState("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [extraStopEnabled, setExtraStopEnabled] = useState(false);
  const [extraStop, setExtraStop] = useState(null);
  const [pendingMapChoice, setPendingMapChoice] = useState(null);
  const [forSomeoneElse, setForSomeoneElse] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientNote, setRecipientNote] = useState("");
  const [rideOptions, setRideOptions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("nexora-ride-prefs")) || ["Recu automatique"];
    } catch {
      return ["Recu automatique"];
    }
  });

  const currentCity = useMemo(() => cities.find((x) => x.id === user?.city_id), [cities, user?.city_id]);
  const cityCenter = useMemo(
    () => (currentCity ? { lat: currentCity.lat, lng: currentCity.lng } : null),
    [currentCity]
  );
  const mapFallbackCenter = useMemo(
    () => cityCenter || DRC_CENTER,
    [cityCenter]
  );
  const effectiveMapCenter = currentCity
    ? (destination || pickup || currentLocation || mapFallbackCenter)
    : DRC_CENTER;
  const effectiveMapZoom = currentCity ? CITY_MAP_ZOOM : DRC_MAP_ZOOM;
  const cityLat = mapFallbackCenter.lat;
  const cityLng = mapFallbackCenter.lng;
  const vehicleAvailability = useMemo(() => {
    const counts = {};
    for (const category of vehicleCats) {
      counts[category.vehicle_type] = nearbyDrivers.filter((driver) => {
        const accepts = DRIVER_COMPATIBILITY[driver.vehicle_type] || [driver.vehicle_type];
        return accepts.includes(category.vehicle_type);
      }).length;
    }
    return counts;
  }, [nearbyDrivers, vehicleCats]);

  const recommendedVehicle = useMemo(() => {
    const available = vehicleCats.filter((category) => vehicleAvailability[category.vehicle_type] > 0);
    return (available[0] || vehicleCats[0])?.vehicle_type;
  }, [vehicleAvailability, vehicleCats]);

  const haversine = useCallback((a, b) => {
    const R = 6371;
    const p1 = (a.lat * Math.PI) / 180;
    const p2 = (b.lat * Math.PI) / 180;
    const dp = ((b.lat - a.lat) * Math.PI) / 180;
    const dl = ((b.lng - a.lng) * Math.PI) / 180;
    const x = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }, []);
  const outsideCity = pickup && cityCenter && haversine(pickup, cityCenter) > 30;

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(Array.isArray(data) ? data : [])).catch(() => setCities([]));
    api.get("/vehicle-categories").then(({ data }) => {
      const sorted = (Array.isArray(data) ? [...data] : []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setVehicleCats(sorted);
    }).catch(() => setVehicleCats([]));
  }, []);

  const requestCurrentLocation = useCallback(() => {
    if (!cityCenter) {
      toast.error("Choisissez votre ville avant de prendre une position");
      return;
    }
    setLocating(true);
    getPreciseLocation()
      .then((loc) => {
        setCurrentLocation(loc);
        const distFromCity = haversine(loc, cityCenter);
        if (distFromCity > 30) {
          toast.error(
            `Votre position est a ${Math.round(distFromCity)} km de ${currentCity?.name || "votre ville"} - hors zone de service`
          );
          setLocating(false);
          return;
        }
        setPickup(loc);
        if (!destination) setStep(STEPS.DEST);
        toast.success(loc.accuracy_m ? `Position detectee a ${Math.round(loc.accuracy_m)} m` : "Position actuelle detectee");
        setLocating(false);
      })
      .catch((err) => {
        if (err.code === 1) toast.error("Autorisation GPS refusee");
        else toast.error("Impossible de detecter votre position");
        setLocating(false);
      });
  }, [cityCenter, currentCity?.name, destination, haversine]);

  useEffect(() => {
    if (!currentCity || cities.length === 0 || pickup || currentLocation || autoLocationTried) return;
    setAutoLocationTried(true);
    requestCurrentLocation();
  }, [currentCity, cities.length, pickup, currentLocation, autoLocationTried, requestCurrentLocation]);

  useEffect(() => {
    const center = pickup || currentLocation || { lat: cityLat, lng: cityLng };
    if (!center?.lat || !center?.lng) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/drivers/nearby", { params: { lat: center.lat, lng: center.lng, radius_km: 50 } });
        if (alive) setNearbyDrivers(data.drivers || []);
      } catch {
        if (alive) setNearbyDrivers([]);
      }
    };
    load();
    const timer = setInterval(load, 15000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [pickup, currentLocation, cityLat, cityLng]);

  const applyMapPoint = (point, target = step) => {
    if (target === STEPS.PICKUP) {
      setPickup(point);
      setStep(STEPS.DEST);
    } else if (target === STEPS.DEST) {
      setDestination(point);
      setStep(STEPS.REVIEW);
    } else if (target === "extra_stop" || extraStopEnabled) {
      setExtraStop(point);
    } else {
      setDestination(point);
    }
  };

  const handleMapClick = async (pt) => {
    if (!cityCenter) {
      toast.error("Choisissez votre ville avant de selectionner un lieu");
      return;
    }
    // Geo-fence: lock to city within 30km
    const distFromCity = haversine(pt, cityCenter);
    if (distFromCity > 30) {
      toast.error(
        `Ce point est à ${Math.round(distFromCity)} km de ${currentCity?.name || "votre ville"} — hors zone de service`
      );
      return;
    }
    const mapData = await reverseGeocode(pt.lat, pt.lng);
    const point = {
      ...pt,
      address: mapData?.label || `${pt.lat.toFixed(5)}, ${pt.lng.toFixed(5)}`,
      map_data: mapData,
    };
    const address = mapData?.address || {};
    const suggestions = [
      { label: mapData?.short, address: mapData?.label },
      { label: address.road, address: [address.road, address.neighbourhood || address.suburb, address.city].filter(Boolean).join(", ") },
      { label: address.neighbourhood || address.suburb || address.quarter, address: [address.neighbourhood || address.suburb || address.quarter, address.city].filter(Boolean).join(", ") },
      { label: "Coordonnees GPS", address: `${pt.lat.toFixed(6)}, ${pt.lng.toFixed(6)}` },
    ].filter((item, index, list) => item.label && list.findIndex((x) => x.label === item.label) === index);
    setPendingMapChoice({
      point,
      suggestions,
      target: step === STEPS.REVIEW && extraStopEnabled ? "extra_stop" : step,
      title:
        step === STEPS.PICKUP
          ? "Confirmer le depart"
          : step === STEPS.DEST
            ? "Confirmer la destination"
            : extraStopEnabled
              ? "Confirmer l'arret"
              : "Changer la destination",
    });
  };

  const reset = () => {
    setPickup(null);
    setDestination(null);
    setAllEstimates({});
    setEstimate(null);
    setPromoApplied(null);
    setPromoCode("");
    setExtraStop(null);
    setExtraStopEnabled(false);
    setStep(STEPS.PICKUP);
  };

  useEffect(() => {
    const run = async () => {
      if (step !== STEPS.REVIEW || !pickup || !destination || !currentCity || vehicleCats.length === 0) return;
      try {
        const results = await Promise.all(
          vehicleCats.map((c) =>
            api.post("/rides/estimate", {
              pickup,
              destination,
              vehicle_type: c.vehicle_type,
              city_id: currentCity.id,
            })
          )
        );
        const map = {};
        results.forEach((r, i) => (map[vehicleCats[i].vehicle_type] = r.data));
        setAllEstimates(map);
        setEstimate(map[vehicleType]);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    run();
  }, [step, pickup, destination, vehicleCats, vehicleType, currentCity]);

  useEffect(() => {
    setEstimate(allEstimates[vehicleType] || null);
  }, [vehicleType, allEstimates]);

  const applyPromo = async () => {
    if (!promoCode || !estimate) return;
    try {
      const { data } = await api.post("/promos/validate", {
        code: promoCode,
        amount: estimate.price,
        vehicle_type: vehicleType,
      });
      setPromoApplied(data);
      toast.success(`Promo ${data.code} appliquée : -${formatCDF(data.discount)}`);
    } catch (e) {
      setPromoApplied(null);
      toast.error(formatApiError(e));
    }
  };

  const confirm = async () => {
    if (!currentCity) {
      toast.error("Choisissez votre ville avant de commander");
      return;
    }
    if (forSomeoneElse && (!recipientName.trim() || !recipientPhone.trim())) {
      toast.error("Ajoutez le nom et le telephone de la personne a recuperer");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/rides", {
        pickup,
        destination,
        vehicle_type: vehicleType,
        city_id: currentCity.id,
        payment_method: paymentMethod,
        promo_code: promoApplied?.code,
        scheduled_at: scheduleMode === "later" ? scheduledAt || null : null,
        extra_stop: extraStopEnabled ? extraStop : null,
        ride_options: rideOptions,
        is_for_someone_else: forSomeoneElse,
        recipient_name: forSomeoneElse ? recipientName.trim() : null,
        recipient_phone: forSomeoneElse ? recipientPhone.trim() : null,
        recipient_note: forSomeoneElse ? recipientNote.trim() : null,
      });
      toast.success("Course créée — recherche chauffeur");
      navigate(`/app/ride/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const finalPrice = promoApplied && estimate
    ? promoApplied.final_amount
    : estimate?.price;

  const toggleRideOption = (option) => {
    setRideOptions((current) =>
      current.includes(option) ? current.filter((item) => item !== option) : [...current, option]
    );
  };

  return (
    <div className="phone-frame phone-screen-lock relative">
      <MapView
        center={effectiveMapCenter}
        zoom={effectiveMapZoom}
        pickup={pickup}
        destination={destination}
        extraStop={extraStopEnabled ? extraStop : null}
        userLocation={!pickup ? currentLocation : null}
        nearbyDrivers={nearbyDrivers}
        onMapClick={handleMapClick}
        height="100dvh"
        className="phone-map-height"
        showAttribution={false}
      />

      {/* Top controls */}
      <div className="map-top-controls-safe pointer-events-none absolute inset-x-0 z-[800]">
        <div className="map-top-row pointer-events-auto flex items-center justify-between gap-2">
          <button
            onClick={() => navigate("/app")}
            className="map-top-button rounded-full glass-strong p-3 press-down"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="map-top-pill rounded-full glass-strong px-4 py-2 text-[10px] font-bold uppercase tracking-widest">
            {step === STEPS.PICKUP && "1/3 · Départ"}
            {step === STEPS.DEST && "2/3 · Destination"}
            {step === STEPS.REVIEW && "3/3 · Choix véhicule"}
          </div>
          <button
            onClick={reset}
            className="map-top-button rounded-full glass-strong p-3 press-down"
            data-testid="reset-btn"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="map-floating-stack pointer-events-auto mt-2 flex flex-col items-center">
          <button
            onClick={requestCurrentLocation}
            disabled={locating}
            className="map-top-pill rounded-full glass-strong px-3 py-2 text-[10px] font-bold uppercase tracking-widest press-down disabled:opacity-60"
            data-testid="use-current-location"
          >
            <NavigationArrow size={12} weight="fill" className="mr-1 inline text-secondary" />
            {locating ? "Detection..." : "Ma position"}
          </button>
        </div>
        {currentCity && (
          <div className="map-top-pill pointer-events-auto mx-auto mt-2 rounded-full glass-strong px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest" data-testid="city-lock">
            <MapPin size={12} weight="fill" className="mr-1 inline text-primary" />
            Zone {currentCity.name} · 30 km max
          </div>
        )}
      </div>

      {pendingMapChoice && (
        <div className="map-choice-panel-safe absolute inset-x-0 z-[850] mx-auto max-w-[540px] px-3" data-testid="map-choice-panel">
          <div className="glass-strong rounded-3xl p-4 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-primary">
                  Donnees de la carte
                </div>
                <h3 className="font-heading text-xl font-bold">{pendingMapChoice.title}</h3>
              </div>
              <button
                onClick={() => setPendingMapChoice(null)}
                className="rounded-full bg-white/10 p-2 press-down"
                data-testid="map-choice-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="mt-3 rounded-2xl bg-white/5 p-3">
              <div className="text-sm font-bold">
                {pendingMapChoice.point.map_data?.short || "Position selectionnee"}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                {pendingMapChoice.point.address}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] text-muted-foreground">
                <div>
                  <span className="font-bold text-foreground">Lat</span> {pendingMapChoice.point.lat.toFixed(6)}
                </div>
                <div>
                  <span className="font-bold text-foreground">Lng</span> {pendingMapChoice.point.lng.toFixed(6)}
                </div>
                <div>
                  <span className="font-bold text-foreground">Type</span> {pendingMapChoice.point.map_data?.type || "-"}
                </div>
                <div>
                  <span className="font-bold text-foreground">Zone</span>{" "}
                  {pendingMapChoice.point.map_data?.address?.neighbourhood ||
                    pendingMapChoice.point.map_data?.address?.suburb ||
                    pendingMapChoice.point.map_data?.address?.city ||
                    "-"}
                </div>
              </div>
            </div>
            {pendingMapChoice.suggestions?.length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Propositions de lieu
                </div>
                {pendingMapChoice.suggestions.map((suggestion) => (
                  <button
                    key={`${suggestion.label}-${suggestion.address}`}
                    onClick={() => {
                      applyMapPoint(
                        {
                          ...pendingMapChoice.point,
                          address: suggestion.address || suggestion.label,
                          selected_label: suggestion.label,
                        },
                        pendingMapChoice.target
                      );
                      setPendingMapChoice(null);
                    }}
                    className="block w-full rounded-2xl border border-white/10 bg-white/5 p-3 text-left press-down hover:bg-white/10"
                    data-testid="map-place-suggestion"
                  >
                    <div className="text-xs font-bold">{suggestion.label}</div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">{suggestion.address}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => setPendingMapChoice(null)}
                className="py-2 text-xs"
                data-testid="map-choice-cancel"
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  applyMapPoint(pendingMapChoice.point, pendingMapChoice.target);
                  setPendingMapChoice(null);
                }}
                className="py-2 text-xs"
                data-testid="map-choice-confirm"
              >
                Utiliser ce lieu
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom sheet */}
      <div className="phone-bottom-panel absolute inset-x-0 bottom-0 z-[800] mx-auto px-2">
        <AnimatePresence mode="wait">
          {!currentCity && (
            <motion.div key="city" initial={{ y: 200, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 200, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }} className="glass-strong rounded-3xl p-5" data-testid="sheet-city">
              <div className="text-[11px] font-bold uppercase tracking-widest text-primary">Ville du compte requise</div>
              <h2 className="mt-1 font-heading text-2xl font-bold">Definissez votre ville</h2>
              <div className="mt-2 text-sm text-muted-foreground">
                La ville se choisit a la creation du compte. Pour eviter les erreurs, allez dans le profil et selectionnez une ville existante avant de reserver.
              </div>
              <Button
                variant="primary"
                className="mt-3 w-full py-3 text-sm"
                onClick={() => navigate("/app/profile")}
                data-testid="ride-city-profile"
              >
                Aller au profil
              </Button>
            </motion.div>
          )}

          {currentCity && step === STEPS.PICKUP && (
            <motion.div key="s1" initial={{ y: 200, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 200, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }} className="glass-strong rounded-3xl p-5" data-testid="sheet-pickup">
              <div className="text-[11px] font-bold uppercase tracking-widest text-primary">Étape 1</div>
              <h2 className="mt-1 font-heading text-2xl font-bold">
                Où êtes-vous ?
              </h2>
              <div className="mt-3">
                <AddressSearch
                  cityCenter={cityCenter}
                  placeholder="Chercher mon adresse de départ..."
                  onSelect={(r) => {
                    setPickup({ lat: r.lat, lng: r.lng, address: r.label, map_data: r });
                    setStep(STEPS.DEST);
                  }}
                />
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">Ou tapez directement sur la carte ↑</div>
            </motion.div>
          )}

          {currentCity && step === STEPS.DEST && (
            <motion.div key="s2" initial={{ y: 200, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 200, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }} className="glass-strong rounded-3xl p-5" data-testid="sheet-dest">
              <div className="text-[11px] font-bold uppercase tracking-widest text-secondary">Étape 2</div>
              <h2 className="mt-1 font-heading text-2xl font-bold">
                Où allez-vous ?
              </h2>
              <div className="mt-3">
                <AddressSearch
                  cityCenter={cityCenter}
                  placeholder="Chercher ma destination..."
                  onSelect={(r) => {
                    setDestination({ lat: r.lat, lng: r.lng, address: r.label, map_data: r });
                    setStep(STEPS.REVIEW);
                  }}
                />
              </div>
              <div className="mt-2 flex items-center gap-2 rounded-xl bg-white/5 p-2 text-[11px]">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Départ placé ✓ · ou tapez la carte</span>
              </div>
            </motion.div>
          )}

          {currentCity && step === STEPS.REVIEW && (
            <motion.div key="s3" initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }} transition={{ type: "spring", stiffness: 240, damping: 26 }} className="glass-strong rounded-3xl p-3 min-[390px]:p-4" data-testid="sheet-review">
              {/* Vehicles scroll */}
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-widest text-primary">Choisir le véhicule</div>
                  <h2 className="font-heading text-xl font-bold">
                    {estimate ? `${estimate.distance_km} km · ${Math.round(estimate.duration_min)} min` : "…"}
                  </h2>
                </div>
                {estimate?.surge_multiplier > 1 && (
                  <Badge tone="warning">× {estimate.surge_multiplier} pointe</Badge>
                )}
              </div>

              <div className="grid gap-2 pb-2">
                {vehicleCats.map((c) => {
                  const est = allEstimates[c.vehicle_type];
                  const active = vehicleType === c.vehicle_type;
                  const meta = VEHICLE_META[c.vehicle_type] || VEHICLE_META.economy;
                  const Icon = meta.icon;
                  const available = vehicleAvailability[c.vehicle_type] || 0;
                  const eta = available > 0
                    ? Math.max(2, Math.round((est?.duration_min || c.eta_min || 5) * 0.18 + (c.eta_min || 4)))
                    : null;
                  const recommended = c.vehicle_type === recommendedVehicle;
                  return (
                    <button
                      key={c.vehicle_type}
                      onClick={() => setVehicleType(c.vehicle_type)}
                      className={`w-full rounded-2xl border p-3 text-left press-down ${
                        active
                          ? "border-primary/60 bg-primary/15 glow-blue"
                          : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                      data-testid={`select-vehicle-${c.vehicle_type}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/10 ${meta.tone}`}>
                            <Icon size={26} weight="duotone" />
                          </span>
                          <span className="min-w-0">
                            <span className="flex flex-wrap items-center gap-1.5">
                              <span className="font-heading text-lg font-black">{c.name || meta.label}</span>
                              {recommended && <Badge tone="success" className="px-2 py-0.5">Recommande</Badge>}
                              {!available && <Badge tone="warning" className="px-2 py-0.5">Rare</Badge>}
                            </span>
                            <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
                              {c.description || meta.note}
                            </span>
                          </span>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="font-mono text-base font-black">{est ? formatCDF(est.price) : "..."}</div>
                          <div className="text-[10px] text-muted-foreground">{eta ? `${eta} min` : "sur demande"}</div>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-xl bg-black/15 p-2">
                          <Clock size={13} className="mx-auto mb-1" />
                          <div className="text-[9px] font-bold uppercase text-muted-foreground">Arrivee</div>
                          <div className="text-xs font-black">{eta ? `${eta} min` : "-"}</div>
                        </div>
                        <div className="rounded-xl bg-black/15 p-2">
                          <UsersThree size={13} className="mx-auto mb-1" />
                          <div className="text-[9px] font-bold uppercase text-muted-foreground">Places</div>
                          <div className="text-xs font-black">{c.capacity || 4}</div>
                        </div>
                        <div className="rounded-xl bg-black/15 p-2">
                          <ShieldCheck size={13} className="mx-auto mb-1" />
                          <div className="text-[9px] font-bold uppercase text-muted-foreground">Dispo</div>
                          <div className="text-xs font-black">{available}</div>
                        </div>
                      </div>
                      <div className="hidden font-mono text-base font-bold">
                        {est ? formatCDF(est.price) : "…"}
                      </div>
                      <div className="hidden text-[9px] text-muted-foreground">
                        {c.capacity} pers · ~{c.eta_min} min
                      </div>
                    </button>
                  );
                })}
              </div>

              <AiInsightCard title="IA prix et matching" limit={1} compact className="mt-2" />

              <div className="mt-3 rounded-2xl bg-white/5 p-3">
                <button
                  onClick={() => setForSomeoneElse((value) => !value)}
                  className="flex w-full items-center justify-between gap-3 text-left press-down"
                  data-testid="toggle-recipient"
                >
                  <span className="flex items-start gap-3">
                    <span className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-full ${forSomeoneElse ? "bg-primary text-primary-foreground" : "bg-white/10 text-muted-foreground"}`}>
                      <UserFocus size={18} weight="bold" />
                    </span>
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                        Commander pour quelqu'un
                      </span>
                      <span className="text-xs text-foreground">
                        {forSomeoneElse ? "Le chauffeur recuperera une autre personne" : "Ex: maman commande pour son enfant"}
                      </span>
                    </span>
                  </span>
                  <span className={`h-5 w-9 rounded-full p-0.5 transition ${forSomeoneElse ? "bg-primary" : "bg-white/15"}`}>
                    <span className={`block h-4 w-4 rounded-full bg-white transition ${forSomeoneElse ? "translate-x-4" : ""}`} />
                  </span>
                </button>

                {forSomeoneElse && (
                  <div className="mt-3 grid gap-2" data-testid="recipient-fields">
                    <Input
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Nom de la personne a recuperer"
                      data-testid="recipient-name"
                    />
                    <Input
                      value={recipientPhone}
                      onChange={(e) => setRecipientPhone(e.target.value)}
                      placeholder="Telephone de la personne (+243...)"
                      inputMode="tel"
                      data-testid="recipient-phone"
                    />
                    <textarea
                      value={recipientNote}
                      onChange={(e) => setRecipientNote(e.target.value)}
                      placeholder="Consignes chauffeur: ecole, uniforme, responsable a appeler..."
                      rows={2}
                      maxLength={500}
                      className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                      data-testid="recipient-note"
                    />
                  </div>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  { k: "now", l: "Maintenant" },
                  { k: "later", l: "Programmer" },
                ].map((item) => (
                  <button
                    key={item.k}
                    onClick={() => setScheduleMode(item.k)}
                    className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-[10px] font-bold uppercase tracking-widest press-down ${
                      scheduleMode === item.k
                        ? "bg-primary text-primary-foreground"
                        : "bg-white/5 text-muted-foreground"
                    }`}
                    data-testid={`schedule-${item.k}`}
                  >
                    <CalendarBlank size={14} weight="bold" />
                    {item.l}
                  </button>
                ))}
              </div>

              {scheduleMode === "later" && (
                <Input
                  type="datetime-local"
                  className="mt-2"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  data-testid="scheduled-at"
                />
              )}

              <div className="mt-3 rounded-2xl bg-white/5 p-3">
                <button
                  onClick={() => setExtraStopEnabled((v) => !v)}
                  className="flex w-full items-center justify-between gap-3 text-left press-down"
                  data-testid="toggle-extra-stop"
                >
                  <span>
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      Arret supplementaire
                    </span>
                    <span className="text-xs text-foreground">
                      {extraStopEnabled
                        ? extraStop
                          ? "Arret place sur la carte"
                          : "Tapez sur la carte pour placer l'arret"
                        : "Ajouter un arret avant la destination"}
                    </span>
                  </span>
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      extraStopEnabled ? "bg-secondary text-secondary-foreground" : "bg-white/10 text-muted-foreground"
                    }`}
                  >
                    <PlusCircle size={18} weight="bold" />
                  </span>
                </button>
              </div>

              <div className="mt-3">
                <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Options de course
                </div>
                <div className="flex flex-wrap gap-2">
                  {RIDE_OPTIONS.map((option) => {
                    const active = rideOptions.includes(option);
                    return (
                      <button
                        key={option}
                        onClick={() => toggleRideOption(option)}
                        className={`rounded-full border px-3 py-2 text-[10px] font-bold uppercase tracking-widest press-down ${
                          active
                            ? "border-secondary/40 bg-secondary/15 text-secondary"
                            : "border-white/10 bg-white/5 text-muted-foreground"
                        }`}
                        data-testid={`ride-option-${option.toLowerCase().replaceAll(" ", "-")}`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Promo */}
              <div className="mt-3 flex items-center gap-2">
                <div className="relative flex-1">
                  <Ticket size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" weight="bold" />
                  <Input
                    className="pl-9"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Code promo (ex: NEXORA10)"
                    data-testid="promo-input"
                  />
                </div>
                <Button variant="outline" onClick={applyPromo} className="text-xs" data-testid="promo-apply">
                  Appliquer
                </Button>
              </div>

              {/* Payment */}
              <div className="mt-3">
                <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Paiement</div>
                <div className="grid grid-cols-2 gap-1.5 min-[390px]:grid-cols-4">
                  {[
                    { k: "cash", l: "Cash" },
                    { k: "mpesa", l: "M-Pesa" },
                    { k: "airtel", l: "Airtel" },
                    { k: "orange", l: "Orange" },
                  ].map((p) => (
                    <button
                      key={p.k}
                      onClick={() => setPaymentMethod(p.k)}
                      className={`rounded-xl py-2 text-[10px] font-bold uppercase tracking-widest transition-all ${
                        paymentMethod === p.k
                          ? "bg-secondary text-secondary-foreground"
                          : "bg-white/5 text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`pay-${p.k}`}
                    >
                      {p.l}
                    </button>
                  ))}
                </div>
                {["mpesa", "airtel", "orange", "cash"].includes(paymentMethod) && (
                  <div className="mt-2 rounded-xl bg-secondary/10 p-2 text-[11px] text-muted-foreground">
                    Paiement direct au chauffeur. Le chauffeur confirme la reception apres la course.
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="mt-3 rounded-2xl bg-white/5 p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span className="font-mono font-bold">{estimate ? formatCDF(estimate.price) : "—"}</span>
                </div>
                {promoApplied && (
                  <div className="flex items-center justify-between text-sm text-secondary">
                    <span>Promo {promoApplied.code}</span>
                    <span className="font-mono font-bold">-{formatCDF(promoApplied.discount)}</span>
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between border-t border-white/10 pt-2 text-base">
                  <span className="font-bold">Total</span>
                  <span className="font-heading font-bold text-primary">
                    {estimate ? formatCDF(finalPrice) : "—"}
                  </span>
                </div>
              </div>

              <Button
                variant="primary"
                className="mt-3 w-full py-4 text-base"
                onClick={confirm}
                disabled={!estimate || creating}
                data-testid="confirm-ride"
              >
                {creating ? "Création..." : `Commander · ${estimate ? formatCDF(finalPrice) : ""}`}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
